/**
 * The window-manager shell (PXL — closes UI-dependency gap D-5, "no client nav
 * shell"). A launcher bar opens one functional panel per system; panels are
 * draggable, closable windows; hotkeys toggle them; Esc closes the topmost.
 * Pure client-local presentation — nothing here rides the socket.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import { isGameKey } from '../game/keyTarget.js';

import { PANELS as ALL_PANELS } from './panels/index.js';
import type { PanelDef } from './panels/index.js';
import { useGuestMode } from '../guest/guestContext.js';
import { guestMenuNewsletter } from '../guest/guestCta.js';
import { GuestEndConfirm } from '../guest/GuestScreens.js';
import { END_CONFIRM_COPY, endConfirmStep } from '../guest/guestEndConfirm.js';
import type { EndConfirmState } from '../guest/guestEndConfirm.js';
import { guestPanelAllowed } from '../guest/guestWalls.js';
import { isCompactLayout } from './layout/layoutMode.js';
import { useLayoutMode } from './layout/useLayout.js';
import { playUiCue } from '../game/audio/index.js';
import { HudIcon } from './HudIcon.js';
import { restack } from './windowStack.js';

interface OpenWindow {
  id: string;
  x: number;
  y: number;
  z: number;
}

/** Window event another surface fires to bring a panel up (open, never toggle closed). */
const OPEN_PANEL_EVENT = 'pxl:open-panel';

/**
 * Ask the Shell to open (or raise) a panel — e.g. the HUD's "[E] Talk" prompt opens the
 * Quests panel, where the NPC's dialogue renders. A no-op when no Shell is mounted.
 */
export function requestOpenPanel(id: string): void {
  window.dispatchEvent(new CustomEvent<string>(OPEN_PANEL_EVENT, { detail: id }));
}

/** Window event a panel fires to close itself (q-demo-dialogue-playtest-2026-10-29). */
const CLOSE_PANEL_EVENT = 'pxl:close-panel';

/**
 * Ask the Shell to close a panel — e.g. the NPC conversation's "goodbye" closes the Quests
 * window the talk opened (owner playtest 2026-10-29: "Goodbye doesn't close the NPC interaction
 * popup"). A no-op when the panel is not open or no Shell is mounted.
 */
export function requestClosePanel(id: string): void {
  window.dispatchEvent(new CustomEvent<string>(CLOSE_PANEL_EVENT, { detail: id }));
}

export function Shell({
  captureSuppressed = false,
  initialMenuOpen = false,
}: {
  captureSuppressed?: boolean;
  /** The compact Menu sheet starts open (a test renders the sheet's buttons server-side). */
  initialMenuOpen?: boolean;
} = {}): JSX.Element {
  const [open, setOpen] = useState<OpenWindow[]>([]);
  // AD-861: a window opening / closing plays its UI cue (code-synthesised, no file).
  const openCount = useRef(0);
  useEffect(() => {
    if (open.length > openCount.current) playUiCue('ui.open');
    else if (open.length < openCount.current) playUiCue('ui.close');
    openCount.current = open.length;
  }, [open.length]);
  // Portrait / short-landscape (mobile-layout.md §3.4): the launcher folds behind one Menu
  // button and a panel opens as a sheet — one at a time, since a sheet covers the last one.
  const compact = isCompactLayout(useLayoutMode());
  const [menuOpen, setMenuOpen] = useState(initialMenuOpen);
  // AD-794 §6.2: a guest's launcher and hotkeys offer only the panels a guest can use; the walled
  // ones (trade, market, store, social, guild, mail, housing, endgame, dev tools) are not there.
  const guest = useGuestMode();
  // Owner 2026-10-24 (guest-demo.md §5.6.4): End demo lives in the Menu and asks before it ends.
  const [endConfirm, setEndConfirm] = useState<EndConfirmState>({ phase: 'idle' });
  const cancelEnd = useCallback(() => setEndConfirm({ phase: 'idle' }), []);
  // The newsletter stays one Menu tap away for the whole demo, whichever launch mode leads the banner.
  const newsletter = guest ? guestMenuNewsletter(guest.ctaTargets) : null;
  const PANELS = useMemo(
    () => (guest ? ALL_PANELS.filter((p) => guestPanelAllowed(p.id)) : ALL_PANELS),
    [guest],
  );

  const focus = useCallback((id: string) => {
    setOpen((ws) => restack(ws, id));
  }, []);

  const close = useCallback((id: string) => {
    setOpen((ws) => ws.filter((w) => w.id !== id));
  }, []);

  const toggle = useCallback(
    (id: string) => {
      setOpen((ws) => {
        if (ws.some((w) => w.id === id)) return ws.filter((w) => w.id !== id);
        const base = compact ? [] : ws;
        const n = base.length;
        return restack(
          [...base, { id, x: 120 + ((n * 32) % 360), y: 70 + ((n * 28) % 240), z: 0 }],
          id,
        );
      });
    },
    [compact],
  );

  const openOrRaise = useCallback(
    (id: string) => {
      setOpen((ws) => {
        if (ws.some((w) => w.id === id)) return restack(ws, id);
        const base = compact ? [] : ws;
        const n = base.length;
        return restack(
          [...base, { id, x: 120 + ((n * 32) % 360), y: 70 + ((n * 28) % 240), z: 0 }],
          id,
        );
      });
    },
    [compact],
  );

  useEffect(() => {
    if (captureSuppressed) return undefined;
    const onOpen = (e: Event): void => {
      const id = (e as CustomEvent<unknown>).detail;
      if (typeof id === 'string' && PANELS.some((p) => p.id === id)) openOrRaise(id);
    };
    window.addEventListener(OPEN_PANEL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PANEL_EVENT, onOpen);
  }, [openOrRaise, captureSuppressed, PANELS]);

  useEffect(() => {
    const onClose = (e: Event): void => {
      const id = (e as CustomEvent<unknown>).detail;
      if (typeof id === 'string') close(id);
    };
    window.addEventListener(CLOSE_PANEL_EVENT, onClose);
    return () => window.removeEventListener(CLOSE_PANEL_EVENT, onClose);
  }, [close]);

  // Hotkeys: toggle a panel by its key; Esc closes the topmost open window.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // A field owns its keys; Ctrl+C / Ctrl+P / Cmd+I are the browser's, not panel toggles.
      if (!isGameKey(e, document.activeElement)) return;
      if (e.key === 'Escape') {
        setOpen((ws) => {
          if (ws.length === 0) return ws;
          const top = ws.reduce((a, b) => (b.z > a.z ? b : a));
          return ws.filter((w) => w.id !== top.id);
        });
        return;
      }
      const def = PANELS.find((p) => p.hotkey.toLowerCase() === e.key.toLowerCase());
      if (def) {
        e.preventDefault();
        toggle(def.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle, PANELS]);

  // E1.5 capture mode: close every open window (incl. Debug) and hide the
  // launcher so the DOM is footage-clean. Window STATE is dropped, not persisted
  // — a clean re-open after capture is fine; nothing may remain in frame.
  useEffect(() => {
    if (captureSuppressed) setOpen([]);
  }, [captureSuppressed]);

  if (captureSuppressed) return <></>;

  return (
    <>
      {open.map((w) => {
        const def = PANELS.find((p) => p.id === w.id);
        if (!def) return null;
        return (
          <Window
            key={w.id}
            def={def}
            frame={w}
            onClose={() => close(w.id)}
            onFocus={() => focus(w.id)}
            onMove={(x, y) => setOpen((ws) => ws.map((o) => (o.id === w.id ? { ...o, x, y } : o)))}
          />
        );
      })}
      {compact ? (
        <button
          type="button"
          className={`pxl-menu-btn${menuOpen ? ' open' : ''}`}
          aria-expanded={menuOpen}
          aria-label="Menu"
          title="Menu"
          onClick={() => setMenuOpen((v) => !v)}
          data-testid="launcher-menu"
        >
          <HudIcon id="menu" size={26} />
        </button>
      ) : null}
      {compact && !menuOpen ? null : (
        <div className={`pxl-launcher${compact ? ' sheet' : ''}`}>
          {PANELS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`pxl-launch-btn${open.some((w) => w.id === p.id) ? ' open' : ''}`}
              // The lane reads this title and strips " (HOTKEY)" (tools/visual-lane/cli.mjs).
              title={`${p.title} (${p.hotkey.toUpperCase()})`}
              aria-label={p.title}
              onClick={() => {
                toggle(p.id);
                if (compact) setMenuOpen(false);
              }}
              data-testid={`launch-${p.id}`}
            >
              {/* q-hud-panel-button-icons: the panel's icon, never a two-letter label ("Ch"). */}
              <HudIcon id={p.id} size={compact ? 26 : 24} />
              {compact ? (
                <span className="nm">{p.title}</span>
              ) : (
                <span className="hk">{p.hotkey.toUpperCase()}</span>
              )}
            </button>
          ))}
          {newsletter ? (
            // A plain link in the Menu, never a prompt: it opens in a new tab so the demo keeps
            // its place (the end card's own rule, guestCta.ts).
            <a
              className="pxl-launch-btn guest-newsletter-btn"
              href={newsletter.href}
              target="_blank"
              rel="noopener noreferrer"
              title={newsletter.label}
              aria-label={`${newsletter.label} (opens in a new tab)`}
              data-cta="newsletter"
              data-testid="guest-newsletter"
              onClick={() => {
                if (compact) setMenuOpen(false);
              }}
            >
              <HudIcon id="mail" size={compact ? 26 : 24} />
              <span className="nm">{newsletter.label}</span>
            </a>
          ) : null}
          {guest ? (
            <button
              type="button"
              className="pxl-launch-btn guest-end-demo-btn"
              title={END_CONFIRM_COPY.menuLabel}
              aria-label={END_CONFIRM_COPY.menuLabel}
              onClick={() => {
                setEndConfirm((st) => endConfirmStep(st, { t: 'open', now: Date.now() }).state);
                if (compact) setMenuOpen(false);
              }}
              data-testid="guest-end-demo"
            >
              <HudIcon id="end-demo" size={compact ? 26 : 24} />
              <span className="nm">{END_CONFIRM_COPY.menuLabel}</span>
            </button>
          ) : null}
        </div>
      )}
      {guest && endConfirm.phase === 'asking' ? (
        <GuestEndConfirm
          onCancel={cancelEnd}
          onConfirm={() => {
            const step = endConfirmStep(endConfirm, { t: 'confirm', now: Date.now() });
            setEndConfirm(step.state);
            if (step.end) guest.onEnd('left');
          }}
        />
      ) : null}
    </>
  );
}

export function Window({
  def,
  frame,
  onClose,
  onFocus,
  onMove,
}: {
  def: PanelDef;
  frame: OpenWindow;
  onClose: () => void;
  onFocus: () => void;
  onMove: (x: number, y: number) => void;
}): JSX.Element {
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  const onPointerDown = (e: ReactPointerEvent): void => {
    onFocus();
    drag.current = { dx: e.clientX - frame.x, dy: e.clientY - frame.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent): void => {
    if (!drag.current) return;
    onMove(
      Math.max(0, Math.min(window.innerWidth - 80, e.clientX - drag.current.dx)),
      Math.max(0, Math.min(window.innerHeight - 40, e.clientY - drag.current.dy)),
    );
  };
  const onPointerUp = (): void => {
    drag.current = null;
  };

  const Body = def.Component;
  return (
    <div
      className="pxl-window"
      style={{ left: frame.x, top: frame.y, zIndex: frame.z, width: def.width ?? 360 }}
      onMouseDown={onFocus}
    >
      <div
        className="pxl-window-title"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <HudIcon id={def.id} size={18} />
        <span className="grow">{def.title}</span>
        <span className="pxl-faint">{def.hotkey.toUpperCase()}</span>
        <button
          type="button"
          className="pxl-x"
          onClick={onClose}
          title="close (Esc)"
          aria-label={`Close ${def.title}`}
        >
          <HudIcon id="close" size={14} />
        </button>
      </div>
      <div className="pxl-window-body">
        <Body />
      </div>
    </div>
  );
}
